import axios from "axios";
import { toast } from "sonner";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
export let API = BASE;
export const api = axios.create({ baseURL: BASE, withCredentials: true, headers: { "X-Dopamine-UI": "1" } });
// Site opened on its own domain (e.g. dopamineagencyllc.com): use that same address when it answers — first-party
// cookies (Safari never drops the session) and no cross-site CORS preflight. Otherwise REACT_APP_BACKEND_URL as before.
export const apiReady = (async () => {
  const same = `${window.location.origin}/api`;
  if (same === BASE || window.location.hostname === "localhost") return;
  try {
    const ctl = new AbortController();
    const t = setTimeout(() => ctl.abort(), 2500);
    const r = await fetch(`${same}/health`, { signal: ctl.signal, credentials: "include" });
    clearTimeout(t);
    if (r.ok && (r.headers.get("content-type") || "").includes("json")) { API = same; api.defaults.baseURL = same; }
  } catch { /* keep REACT_APP_BACKEND_URL */ }
})();

// Account shown by THIS tab: the server refuses (409) a request whose browser cookie now belongs to another account.
let sessionUid = null;
export const setSessionUid = (id) => { sessionUid = id || null; };
// 'Solo visualizzazione' account: no write ever leaves this browser (the server refuses them with 403 anyway).
let readOnly = false;
export const setReadOnly = (v) => { readOnly = !!v; };
export const VIEWER_MSG = "Account in sola visualizzazione: azione non consentita";
api.interceptors.request.use((cfg) => {
  if (sessionUid) cfg.headers["X-Dopamine-User"] = sessionUid;
  // Only exception for 'Solo visualizzazione': requesting a chat export (the server allows exactly this one write).
  const exportReq = /^post$/i.test(cfg.method || "") && /^\/?exports(\/[A-Za-z0-9]+\/cancel)?\/?$/.test(cfg.url || "");
  if (readOnly && !exportReq && !/^(get|head|options)$/i.test(cfg.method || "get") && !/\/auth\//.test(cfg.url || "")) {
    const e = new Error(VIEWER_MSG);
    e.response = { status: 403, data: { detail: VIEWER_MSG } };
    return Promise.reject(e);
  }
  return cfg;
});

let refreshing = null;
const TRANSIENT = [502, 503, 504, 520, 521, 522, 523, 524];
const isTransient = (e) => !e.response || TRANSIENT.includes(e.response.status);
const wait = (ms) => new Promise((r) => setTimeout(r, ms));
// Silent token renewal: a temporary server/network problem is retried; only a real refusal (401/403) ends the session.
const renew = async () => {
  for (let i = 0; ; i += 1) {
    try { return await api.post("/auth/refresh"); } catch (e) {
      // One more try after a refusal: another tab may have renewed the shared cookie a moment ago.
      if (e.response?.status === 401 && i === 0) { await wait(400); continue; }
      if (!isTransient(e) || i >= 4) throw e;
      await wait(800 * (i + 1));
    }
  }
};
api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const cfg = err.config || {};
    const url = cfg.url || "";
    if (err.response?.status === 409 && err.response.data?.detail === "account_changed") {
      window.dispatchEvent(new Event("auth:switched"));
      return Promise.reject(err);
    }
    if (err.response?.status === 401 && !cfg._retry && !url.includes("/auth/refresh") && !url.includes("/auth/login") && !url.includes("/auth/logout")) {
      cfg._retry = true;
      refreshing = refreshing || renew().finally(() => { refreshing = null; });
      try {
        await refreshing;
        return api(cfg);
      } catch (e) {
        if (!isTransient(e)) window.dispatchEvent(new Event("auth:expired"));
        return Promise.reject(err);
      }
    }
    if (err.response?.status === 403 && err.response.data?.detail === "Cambio password obbligatorio") {
      window.dispatchEvent(new Event("auth:mustchange"));
    }
    // Cloudflare/origin/network errors: ONLY reads and the login are retried automatically (never sends or other writes).
    const method = (cfg.method || "get").toLowerCase();
    const transient = isTransient(err);
    if ((method === "get" || url.includes("/auth/login")) && transient && (cfg._tries || 0) < 3) {
      cfg._tries = (cfg._tries || 0) + 1;
      await wait([1000, 2000, 5000][cfg._tries - 1]);
      return api(cfg);
    }
    return Promise.reject(err);
  }
);

export function errMsg(e) {
  if (!e?.response) return "Server momentaneamente non raggiungibile: riprova tra qualche secondo";
  const d = e.response.data?.detail;
  if (e.response.status >= 500 && typeof d !== "string") return "Il server è momentaneamente sovraccarico o in aggiornamento: riprova tra qualche secondo";
  if (!d) return `Errore ${e.response.status}`;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" · ");
  return d.msg || String(d);
}

// Server really down: the red notice appears only after 10 s without any answer from /api/health; a single slow request is silent.
let downSince = null, probing = false;
const probe = async () => {
  if (probing) return;
  probing = true;
  downSince = downSince || Date.now();
  for (;;) {
    try { const r = await fetch(`${API}/health`, { cache: "no-store" }); if (r.ok) break; } catch { /* still down */ }
    if (Date.now() - downSince > 10000) toast.error("Server momentaneamente non raggiungibile: riprova tra qualche secondo", { id: "server-down", duration: Infinity });
    await wait(2000);
  }
  toast.dismiss("server-down");
  downSince = null;
  probing = false;
};

export const toastErr = (e) => {
  if (isTransient(e)) {
    probe();
    if ((e.config?.method || "get").toLowerCase() === "get") return;  // reads already retried by themselves
    toast.error("Connessione lenta: operazione non confermata, riprova", { id: "write-slow" });
    return;
  }
  toast.error(errMsg(e));
};

export function rangeParams(range, custom = {}) {
  if (range === "day") return { range: "custom", start: custom.start, end: custom.end || custom.start };
  return range === "custom" ? { range, start: custom.start, end: custom.end } : { range };
}

export async function download(path, params, filename) {
  try {
    const r = await api.get(path, { params, responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  } catch (e) {
    toastErr(e);
  }
}
